import { useLingui } from "@lingui/react/macro";
import { useMemo } from "react";

import { assetName, formatPercent, ItemInfo, useAssets } from "@/features/matches";
import { cn } from "@/shared/lib";

import { formatBuyTime } from "../../lib/formatBuyTime";

export type BuildItem = {
  itemId: number;
  // Когда его покупают сильные с этой сборкой, секунды с начала матча.
  atS: number;
  // Не показывать минуту под иконкой (предметы «по ситуации»): она есть в описании.
  hideTime?: boolean;
  // Название под иконкой — там, где предмет надо узнать без наведения («против кого»).
  label?: string;
  // Подпись под иконкой: «от пуль», «в 2,4 раза чаще».
  caption?: string;
  // Подпись под временем: «у тебя 30:04», «не куплен».
  note?: string;
  // Доля сильных с этой сборкой, которые его берут: «91 %» под временем.
  pickRate?: number;
  // Плашка «зачем» в описании предмета: «Ослабить врага · режет лечение».
  why?: string;
  // Своя строка внизу описания вместо «Берут N % сильных с этой сборкой».
  footer?: string;
};

// От этой доли купивших предмет берут почти всегда (ключевой): подпись доли — ярче.
const CORE_PICK_RATE = 0.6;

type BuildItemsProps = {
  items: readonly BuildItem[];
  size?: number;
  // Ширина места под предмет: с подписью под иконкой — шире иконки.
  width?: number;
  // Показывать долю под иконкой (по умолчанию — если она известна).
  showPickRate?: boolean;
  className?: string;
};

const SLOT_BORDER = {
  weapon: "border-slot-weapon",
  vitality: "border-slot-vitality",
  spirit: "border-slot-spirit",
} as const;

// Предметы сборки по порядку покупки: иконка в рамке цвета ветки магазина и минута, к которой
// его берут сильные с этой сборкой. Название — в подсказке и для экранного диктора. По нажатию
// (на компьютере — и наведению) на иконку — описание предмета (`ItemInfo`), с долей сильных,
// если она известна. На странице героя — без минуты, с подписью «зачем» под иконкой.
export const BuildItems = ({
  items,
  size = 52,
  width,
  showPickRate = true,
  className,
}: BuildItemsProps) => {
  const { i18n, t } = useLingui();
  const { data } = useAssets();
  const lookup = useMemo(() => new Map((data?.items ?? []).map((item) => [item.id, item])), [data]);
  return (
    <ol className={cn("flex flex-wrap gap-2.5", className)}>
      {items.map(({ itemId, atS, hideTime, label, caption, note, pickRate, why, footer }) => {
        const item = lookup.get(itemId);
        const name = item === undefined ? `#${itemId}` : assetName(item.name);
        const share = pickRate === undefined ? null : formatPercent(pickRate, i18n.locale);
        const time = formatBuyTime(atS);
        return (
          <li
            key={itemId}
            title={name}
            className="flex flex-col items-center gap-0.5 text-center"
            style={{ width: width ?? Math.max(size, 56) }}
          >
            {item?.icon_url ? (
              <ItemInfo
                itemId={itemId}
                name={name}
                className="rounded-[10px]"
                note={why}
                footer={
                  footer ??
                  (share === null
                    ? undefined
                    : t`Taken by ${share} with this build · on average ${time}`)
                }
              >
                <img
                  src={item.icon_url}
                  alt={name}
                  width={size}
                  height={size}
                  loading="lazy"
                  className={cn(
                    "rounded-[10px] border-[3px] bg-surface-raised object-cover",
                    item.slot === null ? "border-border" : SLOT_BORDER[item.slot],
                  )}
                />
              </ItemInfo>
            ) : (
              <span
                role="img"
                aria-label={name}
                style={{ width: size, height: size }}
                className="inline-flex items-center justify-center rounded-[10px] border-[3px] border-border bg-surface-raised font-display text-muted"
              >
                {name.slice(0, 1)}
              </span>
            )}
            {label !== undefined && (
              <span className="mt-0.5 max-w-full text-[11.5px] leading-tight font-semibold [overflow-wrap:anywhere] hyphens-auto text-foreground">
                {label}
              </span>
            )}
            {hideTime !== true && (
              <span className="font-mono text-xs text-foreground-soft">{time}</span>
            )}
            {caption !== undefined && (
              // Длинное слово («скорострельность») шире иконки: переносится по слогам, а не
              // наезжает на соседний предмет.
              <span className="max-w-full text-[11px] leading-tight [overflow-wrap:anywhere] hyphens-auto text-muted">
                {caption}
              </span>
            )}
            {showPickRate && pickRate !== undefined && (
              <span
                className={cn(
                  "text-[11px] whitespace-nowrap",
                  pickRate >= CORE_PICK_RATE ? "text-muted" : "text-neutral",
                )}
              >
                {share}
              </span>
            )}
            {/* «у тебя 13:15» шире иконки: переносится, а не наезжает на соседний предмет. */}
            {note !== undefined && (
              <span className="text-[11px] leading-tight text-warn">{note}</span>
            )}
          </li>
        );
      })}
    </ol>
  );
};
